import { useEffect, useState } from 'react';
import { metaApi, seerrApi, type SeerrRequest } from '../api';
import { useApp } from '../context';
import { navigate } from '../router';

/**
 * Houdt je Seerr-verzoeken in de gaten en meldt het zodra er een binnen is
 * ("Je aanvraag … staat klaar"), met een knop om meteen te kijken.
 * Controleert bij het openen en daarna elke 10 minuten; de vorige stand staat per profiel in de browser.
 */

interface Ready {
  id: number;
  title: string;
  poster: string | null;
  ratingKey: string | null;
  partial: boolean;
}

const INTERVAL = 10 * 60_000;

export function RequestWatcher() {
  const { me } = useApp();
  const key = `tumedia.requestState.${me.profile?.uuid ?? 'x'}`;
  const [ready, setReady] = useState<Ready[]>([]);

  useEffect(() => {
    let live = true;
    const check = async () => {
      const st = await seerrApi.status().catch(() => null);
      if (!st?.configured) return;
      const r = await seerrApi.requests().catch(() => null);
      if (!r?.linked || !live) return;

      let prev: Record<string, string> = {};
      let firstRun = false;
      try {
        const raw = localStorage.getItem(key);
        firstRun = raw === null;
        prev = JSON.parse(raw ?? '{}') as Record<string, string>;
      } catch {
        firstRun = true;
      }
      const now: Record<string, string> = {};
      const fresh: SeerrRequest[] = [];
      for (const q of r.requests) {
        const status = q.media?.status ?? 'none';
        now[String(q.id)] = status;
        const before = prev[String(q.id)];
        const done = status === 'available' || status === 'partial';
        // Eerste keer: alleen de stand onthouden, niet alles in één keer melden
        if (!firstRun && done && before !== undefined && before !== status) fresh.push(q);
      }
      try {
        localStorage.setItem(key, JSON.stringify(now));
      } catch {
        // niet erg
      }
      if (!fresh.length) return;

      const found = await metaApi.inLibrary(fresh.filter((q) => q.media).map((q) => ({ type: q.media!.mediaType, tmdbId: q.media!.tmdbId, title: q.media!.title, year: q.media!.year })))
        .catch(() => ({ found: {} as Record<string, string | null> }));
      const items: Ready[] = fresh.map((q) => ({
        id: q.id,
        title: q.media!.title,
        poster: q.media!.poster,
        ratingKey: found.found[`${q.media!.mediaType}:${q.media!.tmdbId}`] ?? null,
        partial: q.media!.status === 'partial',
      }));
      if (!live) return;
      setReady((cur) => [...cur, ...items.filter((x) => !cur.some((c) => c.id === x.id))]);
      if ('Notification' in window && Notification.permission === 'granted') {
        for (const x of items) {
          try {
            new Notification(`${x.title} staat klaar`, { body: x.partial ? 'Een deel van je aanvraag is binnen.' : 'Je aanvraag is binnen in Plex.', tag: `req-${x.id}` });
          } catch {
            // alleen via service worker toegestaan in sommige browsers
          }
        }
      }
    };
    const first = window.setTimeout(() => void check(), 5000);
    const t = window.setInterval(() => void check(), INTERVAL);
    return () => {
      live = false;
      window.clearTimeout(first);
      window.clearInterval(t);
    };
  }, [key]);

  const close = (id: number) => setReady((r) => r.filter((x) => x.id !== id));

  if (!ready.length) return null;
  return (
    <div className="toasts toasts-left" role="region" aria-label="Verzoeken" aria-live="polite">
      {ready.map((x) => (
        <div key={x.id} className="toast toast-req">
          {x.poster && <img src={x.poster} alt="" className="toast-poster" />}
          <div className="toast-body">
            <span className="subtle">{x.partial ? 'Deels binnen' : 'Je aanvraag staat klaar'}</span>
            <strong>{x.title}</strong>
            <div className="form-actions">
              {x.ratingKey && (
                <button className="btn btn-primary btn-sm" onClick={() => { close(x.id); navigate(`/item/${x.ratingKey}`); }}>Bekijken</button>
              )}
              <button className="btn btn-sm" onClick={() => close(x.id)}>Sluiten</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
